import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Leaf, Eye, EyeSlash, ArrowLeft, Check } from '@phosphor-icons/react';

export default function RegisterPage() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!name || !email || !password || !confirmPassword) {
            toast.error('Please fill in all fields');
            return;
        }
        
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        
        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        if (!agreeTerms) {
            toast.error('Please agree to the terms and conditions');
            return;
        }
        
        setLoading(true);
        try {
            await register(email, password, name);
            toast.success('Welcome to Bush Rangers!');
            navigate('/dashboard');
        } catch (error) {
            console.error('Register error:', error);
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4 py-8">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                {/* Back Link */}
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-[#475569] hover:text-[#22c55e] mb-6 font-semibold"
                    data-testid="back-to-home"
                >
                    <ArrowLeft weight="bold" size={20} />
                    Back to Home
                </Link>
                
                <div className="brutalist-card p-6 md:p-8">
                    {/* Logo */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center mb-4">
                            <Leaf weight="fill" className="text-white" size={32} />
                        </div>
                        <h1 className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b]">
                            Join Bush Rangers
                        </h1>
                        <p className="text-[#475569] mt-2">
                            Create a parent account to get started
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                Your Name
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="brutalist-input w-full"
                                placeholder="Enter your name"
                                data-testid="register-name"
                            />
                        </div>

                        <div>
                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="brutalist-input w-full"
                                placeholder="your@email.com"
                                data-testid="register-email"
                            />
                        </div>

                        <div>
                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="brutalist-input w-full pr-12"
                                    placeholder="Min 6 characters"
                                    data-testid="register-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#1e293b]"
                                >
                                    {showPassword ? <EyeSlash size={24} /> : <Eye size={24} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                Confirm Password
                            </label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="brutalist-input w-full"
                                placeholder="Confirm your password"
                                data-testid="register-confirm-password"
                            />
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer" onClick={() => setAgreeTerms(!agreeTerms)} data-testid="register-terms-label">
                            <input 
                                type="checkbox"
                                checked={agreeTerms}
                                onChange={(e) => setAgreeTerms(e.target.checked)}
                                className="sr-only"
                                data-testid="register-terms-checkbox"
                            />
                            <div 
                                className={`w-6 h-6 rounded-md border-4 border-[#1e293b] flex items-center justify-center flex-shrink-0 mt-0.5 ${agreeTerms ? 'bg-[#22c55e]' : 'bg-white'}`}
                                data-testid="register-terms"
                            >
                                {agreeTerms && <Check weight="bold" className="text-white" size={16} />}
                            </div>
                            <span className="text-sm text-[#475569]">
                                I agree to the <a href="#" className="text-[#22c55e] underline">Terms of Service</a> and <a href="#" className="text-[#22c55e] underline">Privacy Policy</a>. I confirm I am an adult creating this account for my child.
                            </span>
                        </label>

                        <motion.button
                            type="submit"
                            disabled={loading}
                            whileHover={{ y: -2, x: -2 }}
                            whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn w-full bg-[#22c55e] text-white text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="register-submit"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Creating Account...
                                </span>
                            ) : (
                                'Create Account'
                            )}
                        </motion.button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-[#475569]">
                            Already have an account?{' '}
                            <Link 
                                to="/login" 
                                className="text-[#22c55e] font-semibold hover:underline"
                                data-testid="login-link"
                            >
                                Log In
                            </Link>
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
